import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import PlanSelectDialog from "@/components/PlanSelectDialog";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { Crown, Truck, Users, HandCoins, Calendar, CheckCircle, Warning, Gift, Copy, WhatsappLogo, TelegramLogo, FacebookLogo, XLogo, Envelope, PaperPlaneTilt, Wallet, Clock, XCircle } from "@phosphor-icons/react";
import LinkStatsCard from "@/components/LinkStatsCard";

const ROLE_LABEL = { company: "Empresa", driver: "Motorista", autonomo: "Motorista Autônomo" };

export default function MeuPlano() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [planOpen, setPlanOpen] = useState(false);
  const [referral, setReferral] = useState(null);

  const load = async () => {
    try { const { data } = await api.get("/plan/status"); setStatus(data); } catch {}
    try { const { data } = await api.get("/referral/info"); setReferral(data); } catch {}
  };
  useEffect(() => { load(); const iv = setInterval(load, 30000); return () => clearInterval(iv); }, []);

  const copyReferral = async () => {
    if (!referral?.link) return;
    try { await navigator.clipboard.writeText(referral.link); toast.success("Link copiado!"); } catch {}
  };
  const shareWhatsApp = () => {
    if (!referral?.link) return;
    const isCompany = user?.role === "company";
    const msg = isCompany
      ? `Oi! Sou cliente do Acerto Rápido JVO — app pra motoristas gerenciarem viagens, fretes e acerto. Se você usar meu link, ganha 50% OFF no 2º mês e eu também ganho 50%. Testa 7 dias grátis: ${referral.link}`
      : `Oi! Uso um app chamado Acerto Rápido JVO pro meu acerto de viagens. Se cadastrar sua empresa pelo meu link, você ganha 50% OFF no 2º mês e eu também. Testa 7 dias grátis: ${referral.link}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };
  const shareText = () => {
    const isCompany = user?.role === "company";
    return isCompany
      ? `Uso o Acerto Rápido JVO no meu caminhão — resolve viagem, frete e acerto num app só. Testa 7 dias grátis e ganha 50% OFF no 2º mês pelo meu link: ${referral?.link || ""}`
      : `Uso o Acerto Rápido JVO pro meu acerto de viagens. 7 dias grátis + 50% OFF no 2º mês pelo meu link: ${referral?.link || ""}`;
  };
  const shareTelegram = () => referral?.link && window.open(`https://t.me/share/url?url=${encodeURIComponent(referral.link)}&text=${encodeURIComponent(shareText())}`, "_blank");
  const shareFacebook = () => referral?.link && window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(referral.link)}&quote=${encodeURIComponent(shareText())}`, "_blank");
  const shareX = () => referral?.link && window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText())}`, "_blank");
  const shareEmail = () => referral?.link && window.open(`mailto:?subject=${encodeURIComponent("Acerto Rápido JVO — 50% OFF pelo meu link")}&body=${encodeURIComponent(shareText())}`, "_blank");
  const shareNative = async () => {
    if (!referral?.link) return;
    if (navigator.share) {
      try { await navigator.share({ title: "Acerto Rápido JVO", text: shareText(), url: referral.link }); } catch {}
    } else {
      await copyReferral();
    }
  };

  const fmtBRL = (n) => (Number(n) || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  const fmtDateBR = (iso) => {
    if (!iso) return "-";
    try { return new Date(iso).toLocaleDateString("pt-BR"); } catch { return "-"; }
  };
  const statusBadge = (s) => {
    if (s === "ativa") return { cls: "bg-emerald-100 text-emerald-800 border-emerald-300", label: "🟢 Ativa", Icon: CheckCircle };
    if (s === "expirada") return { cls: "bg-red-100 text-red-800 border-red-300", label: "🔴 Expirada", Icon: XCircle };
    return { cls: "bg-amber-100 text-amber-800 border-amber-300", label: "🟡 Pendente", Icon: Clock };
  };

  if (!status) return <p className="text-muted-foreground">Carregando…</p>;

  const vehiclePct = Math.min(100, (status.vehicles_used / Math.max(1, status.vehicles_max)) * 100);
  const driverPct = Math.min(100, (status.drivers_used / Math.max(1, status.drivers_max)) * 100);
  const isAutonomo = status.role === "autonomo";
  const nearLimit = vehiclePct >= 80 || driverPct >= 80;
  const trialExpiringSoon = status.trial_days_left !== null && status.trial_days_left <= 7 && !status.subscription_active;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight flex items-center gap-3" style={{ fontFamily: "Manrope" }}>
          <Crown size={32} weight="duotone" className="text-blue-600" /> Meu Plano
        </h1>
        <p className="text-muted-foreground text-sm mt-1">Consumo, limites e opções de upgrade</p>
      </div>

      {/* Alerts */}
      {trialExpiringSoon && (
        <Card className="border-2 border-amber-400 bg-amber-50" data-testid="alert-trial-expiring">
          <CardContent className="p-4 flex items-center justify-between gap-3">
            <div className="flex gap-3 items-center">
              <Warning size={24} weight="fill" className="text-amber-600 shrink-0" />
              <div>
                <div className="font-bold text-amber-900">Seu trial termina em {status.trial_days_left} dia(s)!</div>
                <div className="text-sm text-amber-800">Assine agora e não perca acesso.</div>
              </div>
            </div>
            <Button onClick={() => setPlanOpen(true)} data-testid="btn-subscribe-trial" className="bg-amber-500 hover:bg-amber-600 hover:text-black text-black font-bold">Assinar</Button>
          </CardContent>
        </Card>
      )}

      {nearLimit && (
        <Card className="border-2 border-orange-400 bg-orange-50" data-testid="alert-near-limit">
          <CardContent className="p-4 flex items-center justify-between gap-3">
            <div className="flex gap-3 items-center">
              <Warning size={24} weight="fill" className="text-orange-600 shrink-0" />
              <div>
                <div className="font-bold text-orange-900">Você está perto do limite do seu plano</div>
                <div className="text-sm text-orange-800">Considere fazer upgrade para não travar cadastros novos.</div>
              </div>
            </div>
            <Button onClick={() => setPlanOpen(true)} data-testid="btn-upgrade-nearlimit" className="bg-orange-500 hover:bg-orange-600 hover:text-white text-white font-bold">Fazer Upgrade</Button>
          </CardContent>
        </Card>
      )}

      {/* Current Plan Card */}
      <Card>
        <CardHeader className="flex-row items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Crown size={20} weight="duotone" className="text-blue-600" />
            Plano Atual: {ROLE_LABEL[status.role] || status.role}
          </CardTitle>
          {status.subscription_active ? (
            <Badge className="bg-emerald-600 gap-1" data-testid="badge-active"><CheckCircle size={14} weight="fill" /> Assinante Ativo</Badge>
          ) : (
            <Badge variant="secondary" data-testid="badge-trial">
              {status.trial_days_left !== null ? `Trial: ${status.trial_days_left} dia(s)` : "Trial"}
            </Badge>
          )}
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Vehicles progress */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <Truck size={18} weight="duotone" className="text-blue-600" /> Veículos cadastrados
              </div>
              <div className="text-sm font-mono" data-testid="veh-usage">
                <span className="font-extrabold text-lg">{status.vehicles_used}</span>
                <span className="text-muted-foreground"> / {status.vehicles_max}</span>
              </div>
            </div>
            <Progress value={vehiclePct} className={`h-3 ${vehiclePct >= 80 ? "[&>div]:bg-orange-500" : "[&>div]:bg-blue-600"}`} />
          </div>

          {/* Drivers progress (skip for autonomo since limit=1) */}
          {!isAutonomo && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 text-sm font-semibold">
                  <Users size={18} weight="duotone" className="text-blue-600" /> Motoristas cadastrados
                </div>
                <div className="text-sm font-mono" data-testid="drv-usage">
                  <span className="font-extrabold text-lg">{status.drivers_used}</span>
                  <span className="text-muted-foreground"> / {status.drivers_max}</span>
                </div>
              </div>
              <Progress value={driverPct} className={`h-3 ${driverPct >= 80 ? "[&>div]:bg-orange-500" : "[&>div]:bg-blue-600"}`} />
            </div>
          )}

          {/* Trip count */}
          <div className="flex items-center justify-between border-t pt-4">
            <div className="flex items-center gap-2 text-sm font-semibold">
              <HandCoins size={18} weight="duotone" className="text-blue-600" /> Fretes lançados
            </div>
            <div className="text-lg font-extrabold" data-testid="trips-total">{status.trips_total}</div>
          </div>

          {status.trial_ends_at && !status.subscription_active && (
            <div className="flex items-center justify-between text-sm text-muted-foreground border-t pt-4">
              <div className="flex items-center gap-2"><Calendar size={16} /> Trial expira em</div>
              <div>{new Date(status.trial_ends_at).toLocaleDateString("pt-BR")}</div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upgrade Card */}
      <Card className="border-2 border-blue-200 bg-blue-50/40">
        <CardHeader><CardTitle className="text-lg">
          {status.subscription_active ? "Trocar de Plano" : "Escolher Plano"}
        </CardTitle></CardHeader>
        <CardContent className="space-y-4">
          {isAutonomo ? (
            <div className="space-y-3">
              <p className="text-sm text-slate-700">Seu plano atual: <strong>Autônomo (até 5 veículos)</strong>. Se você tem mais veículos ou pretende cadastrar motoristas, precisa do plano <strong>Empresa</strong>.</p>
              <div className="grid md:grid-cols-2 gap-3">
                <div className="border-2 border-blue-300 rounded-lg p-3 bg-white">
                  <div className="text-xs uppercase tracking-wider text-blue-700 font-bold">Autônomo</div>
                  <div className="text-2xl font-extrabold" style={{ fontFamily: "Manrope" }}>R$ 25,90<span className="text-sm font-normal text-muted-foreground">/mês</span></div>
                  <div className="text-xs text-muted-foreground mt-1">Até 5 veículos</div>
                </div>
                <div className="border-2 border-yellow-400 rounded-lg p-3 bg-white relative">
                  <Badge className="absolute -top-2 right-2 bg-yellow-500 text-black text-[10px]">RECOMENDADO</Badge>
                  <div className="text-xs uppercase tracking-wider text-blue-700 font-bold">Empresa</div>
                  <div className="text-2xl font-extrabold" style={{ fontFamily: "Manrope" }}>R$ 59,90<span className="text-sm font-normal text-muted-foreground">/mês</span></div>
                  <div className="text-xs text-muted-foreground mt-1">Até 150 veículos + 150 motoristas</div>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-sm text-slate-700">Empresa: até 150 veículos e 150 motoristas. Para expandir, contate o suporte via aba <strong>Chat</strong>.</p>
          )}
          <Button onClick={() => setPlanOpen(true)} data-testid="btn-open-plans" className="w-full md:w-auto bg-blue-600 hover:bg-blue-700 hover:text-white text-white h-11">
            {status.subscription_active ? "Ver outros planos" : "Ver planos disponíveis"}
          </Button>
        </CardContent>
      </Card>

      <PlanSelectDialog open={planOpen} onOpenChange={setPlanOpen} showAll={isAutonomo} />

      {/* Dashboard de conversão do link /assinar */}
      <LinkStatsCard />

      {/* Bonificações & Indicações */}
      {referral && (
        <Card data-testid="referral-card" className="border-2 border-emerald-300 bg-gradient-to-br from-emerald-50 to-white">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Gift size={22} weight="duotone" className="text-emerald-600" />
              Bonificações & Indicações <span className="text-emerald-700">50% OFF</span>
            </CardTitle>
            <p className="text-xs text-slate-600 mt-1">
              A cada {user?.role === "company" ? "motorista" : "empresa"} que assinar pelo seu link, você e ele ganham <strong>50% OFF</strong> no próximo pagamento. Até <strong>3 indicações válidas</strong>.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Saldo de Bonificação */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2" data-testid="bonus-stats">
              <div className="bg-white border-2 border-emerald-300 rounded-lg p-3 text-center">
                <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold flex items-center justify-center gap-1"><Wallet size={12} weight="fill" /> Economizado</div>
                <div className="text-lg font-extrabold text-emerald-800 mt-1" data-testid="bonus-total-saved">{fmtBRL(referral.total_saved)}</div>
              </div>
              <div className="bg-white border-2 border-amber-300 rounded-lg p-3 text-center">
                <div className="text-[10px] uppercase tracking-wider text-amber-700 font-bold flex items-center justify-center gap-1"><Clock size={12} weight="fill" /> A receber</div>
                <div className="text-lg font-extrabold text-amber-800 mt-1" data-testid="bonus-pending">{fmtBRL(referral.pending_bonus)}</div>
              </div>
              <div className="bg-white border-2 border-emerald-200 rounded-lg p-3 text-center">
                <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-bold">Ativas</div>
                <div className="text-lg font-extrabold text-emerald-800 mt-1" data-testid="bonus-active-count">{referral.active_count || 0}</div>
              </div>
              <div className="bg-white border-2 border-slate-200 rounded-lg p-3 text-center">
                <div className="text-[10px] uppercase tracking-wider text-slate-600 font-bold">Pendentes</div>
                <div className="text-lg font-extrabold text-slate-700 mt-1" data-testid="bonus-pending-count">{referral.pending_count || 0}</div>
              </div>
            </div>

            <div className="grid md:grid-cols-3 gap-4">
              <div className="md:col-span-2 space-y-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-xs uppercase tracking-wider text-slate-500 font-bold">Seu código:</span>
                  <code data-testid="referral-code" className="bg-emerald-100 text-emerald-800 font-extrabold px-3 py-1 rounded-md text-lg">{referral.referral_code}</code>
                </div>
                <div className="flex gap-2">
                  <Input readOnly value={referral.link} data-testid="referral-link" className="text-xs bg-slate-50" />
                  <Button variant="outline" onClick={copyReferral} data-testid="btn-copy-referral" title="Copiar link"><Copy size={16} /></Button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                  <Button onClick={shareWhatsApp} data-testid="btn-share-wa" className="bg-emerald-600 hover:bg-emerald-700 hover:text-white text-white gap-1 h-10">
                    <WhatsappLogo size={16} weight="fill" /> WhatsApp
                  </Button>
                  <Button onClick={shareTelegram} data-testid="btn-share-telegram" variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-50 hover:text-blue-800 gap-1 h-10">
                    <TelegramLogo size={16} weight="fill" /> Telegram
                  </Button>
                  <Button onClick={shareFacebook} data-testid="btn-share-facebook" variant="outline" className="border-blue-300 text-blue-700 hover:bg-blue-50 hover:text-blue-800 gap-1 h-10">
                    <FacebookLogo size={16} weight="fill" /> Facebook
                  </Button>
                  <Button onClick={shareX} data-testid="btn-share-x" variant="outline" className="border-slate-800 text-slate-800 hover:bg-slate-100 gap-1 h-10">
                    <XLogo size={16} weight="fill" /> X (Twitter)
                  </Button>
                  <Button onClick={shareEmail} data-testid="btn-share-email" variant="outline" className="gap-1 h-10">
                    <Envelope size={16} /> E-mail
                  </Button>
                  <Button onClick={shareNative} data-testid="btn-share-native" variant="outline" className="gap-1 h-10">
                    <PaperPlaneTilt size={16} weight="fill" /> Mais…
                  </Button>
                </div>
              </div>
              <div className="flex flex-col items-center justify-center bg-white rounded-lg border-2 border-emerald-200 p-2">
                <QRCodeCanvas value={referral.link} size={120} level="M" fgColor="#047857" />
                <div className="text-[10px] text-slate-500 mt-1">Escanear</div>
              </div>
            </div>

            {/* Progresso das indicações */}
            <div className="border-t pt-4">
              <div className="flex justify-between items-center mb-2">
                <div className="text-sm font-semibold">Indicações válidas</div>
                <div className="text-sm font-mono"><span className="text-2xl font-extrabold text-emerald-700">{referral.count}</span> / {referral.max}</div>
              </div>
              <Progress value={(referral.count / referral.max) * 100} className="h-3 [&>div]:bg-emerald-500" />
              {referral.count >= referral.max ? (
                <p className="text-xs text-slate-500 mt-2">🎉 Você atingiu o máximo de indicações válidas — obrigado!</p>
              ) : (
                <p className="text-xs text-slate-500 mt-2">Faltam <strong>{referral.max - referral.count}</strong> pra ganhar todos os cupons.</p>
              )}
            </div>

            {/* Lista de indicações */}
            <div className="border-t pt-4">
              <div className="text-xs uppercase tracking-wider text-emerald-700 font-bold mb-2">Minhas indicações ({referral.referrals?.length || 0})</div>
              {(!referral.referrals || referral.referrals.length === 0) ? (
                <div className="text-sm text-slate-500 italic bg-slate-50 border rounded p-3">
                  Você ainda não indicou ninguém. Compartilhe seu link acima e ganhe 50% OFF a cada indicação que assinar!
                </div>
              ) : (
                <div className="border rounded-md divide-y" data-testid="referrals-list">
                  {referral.referrals.map((r) => {
                    const b = statusBadge(r.status);
                    return (
                      <div key={r.id} className="flex items-center justify-between p-2.5 text-sm gap-2 flex-wrap" data-testid={`referral-item-${r.status}`}>
                        <div className="flex-1 min-w-[140px]">
                          <div className="font-semibold text-slate-900">{r.name}</div>
                          <div className="text-xs text-slate-500">{r.email_masked} · {fmtDateBR(r.signup_date)}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-xs text-slate-500">Bônus</div>
                          <div className="font-bold text-emerald-700">{fmtBRL(r.bonus_value)}</div>
                        </div>
                        <span className={`text-xs font-bold px-2 py-1 rounded-full border ${b.cls}`}>{b.label}</span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {referral.credits && referral.credits.length > 0 && (
              <div className="border-t pt-4">
                <div className="text-xs uppercase tracking-wider text-emerald-700 font-bold mb-2">Seus cupons</div>
                <div className="space-y-1.5">
                  {referral.credits.map((c) => (
                    <div key={c.coupon_id} className="flex items-center justify-between bg-emerald-50 border border-emerald-200 rounded p-2 text-sm">
                      <div className="flex-1">
                        <div className="text-xs text-slate-600">Indicação de <strong>{c.referred_name}</strong></div>
                        {c.promo_code && <code className="text-emerald-800 font-bold">{c.promo_code}</code>}
                      </div>
                      <Badge className={c.used ? "bg-slate-400" : "bg-emerald-600"}>
                        {c.used ? "Usado" : "50% OFF disponível"}
                      </Badge>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {referral.welcome_promo_code && (
              <div className="border-t pt-4 bg-yellow-50 border-yellow-200 border rounded p-3">
                <div className="text-xs uppercase tracking-wider text-yellow-800 font-bold">🎁 Seu bônus de boas-vindas</div>
                <p className="text-sm mt-1">Use o código <code className="bg-white px-2 py-0.5 rounded font-bold text-yellow-900">{referral.welcome_promo_code}</code> pra <strong>50% OFF</strong> no seu 2º pagamento.</p>
              </div>
            )}

            <div className="border-t pt-3">
              <a href="/como-indicar" target="_blank" rel="noreferrer" className="text-xs text-emerald-700 hover:text-emerald-800 underline font-semibold">
                📖 Ver tutorial completo do programa de indicação →
              </a>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
